/**
 * The search dictionary — `/v1/admin/search/*`.
 *
 * **Two scopes, and the split is the server's.** The reads admit `catalogue_content`, `growth`
 * and `support`, because "what did devotees search for and find nothing" is a question all three
 * ask. Every write is `catalogue_content` alone. These hooks make no scope decision — the screen
 * gates its buttons so nobody is shown a door the API will not open, and the API decides.
 *
 * **A synonym group is edited as rows, not as a set.** Each term is its own row with its own id,
 * so adding one is a POST and removing one is a DELETE — there is no replace-the-whole-group
 * endpoint, and inventing one in the client would mean reading every term back and writing them
 * all again to change one word.
 *
 * **Promotion is one action, not two.** A zero-result query becomes an alias in a single POST, so
 * the miss and the fix cannot drift apart — which is the whole point of keeping the report.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type SynonymRow = components['schemas']['SynonymRow'];
export type AliasRow = components['schemas']['AliasRow'];
export type ZeroResultRow = components['schemas']['ZeroResultRow'];
type SynonymList = components['schemas']['SynonymList'];
type AliasList = components['schemas']['AliasList'];
type ZeroResultList = components['schemas']['ZeroResultList'];

/** The three tables that have an alias column. The server names them in its own refusal. */
export const ALIAS_ENTITIES = ['puja', 'temple', 'city'] as const;
export type AliasEntity = (typeof ALIAS_ENTITIES)[number];

function useSearchMutation<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.search.all() }),
  });
}

export function useSynonyms(groupKey?: string) {
  return useQuery({
    queryKey: keys.search.synonyms(groupKey),
    queryFn: () =>
      authedClient.request<SynonymList>(
        // Only when it has a value: an empty `group_key=` is a filter on the empty string to a
        // server, not the absence of one.
        `/v1/admin/search/synonyms${groupKey ? `?group_key=${encodeURIComponent(groupKey)}` : ''}`,
      ),
  });
}

export function useCreateSynonym() {
  return useSearchMutation((body: { group_key: string; term: string; language: string }) =>
    authedClient.request<SynonymRow>('/v1/admin/search/synonyms', { method: 'POST', body }),
  );
}

export function usePatchSynonym() {
  return useSearchMutation(
    ({ synonymId, body }: { synonymId: number; body: { is_active?: boolean; term?: string } }) =>
      authedClient.request<SynonymRow>(`/v1/admin/search/synonyms/${synonymId}`, {
        method: 'PATCH',
        body,
      }),
  );
}

export function useDeleteSynonym() {
  return useSearchMutation(({ synonymId }: { synonymId: number }) =>
    authedClient.request<void>(`/v1/admin/search/synonyms/${synonymId}`, { method: 'DELETE' }),
  );
}

export function useAliases(entity: AliasEntity, entityId?: number) {
  return useQuery({
    queryKey: keys.search.aliases(entity, entityId),
    queryFn: () =>
      authedClient.request<AliasList>(
        `/v1/admin/search/aliases/${entity}${entityId === undefined ? '' : `?entity_id=${entityId}`}`,
      ),
  });
}

export function useCreateAlias() {
  return useSearchMutation(
    ({
      entity,
      body,
    }: {
      entity: AliasEntity;
      body: { entity_id: number; alias: string; script: string };
    }) =>
      authedClient.request<AliasRow>(`/v1/admin/search/aliases/${entity}`, {
        method: 'POST',
        body,
      }),
  );
}

export function usePatchAlias() {
  return useSearchMutation(
    ({
      entity,
      aliasId,
      body,
    }: {
      entity: AliasEntity;
      aliasId: number;
      body: { is_active?: boolean; alias?: string };
    }) =>
      authedClient.request<AliasRow>(`/v1/admin/search/aliases/${entity}/${aliasId}`, {
        method: 'PATCH',
        body,
      }),
  );
}

export function useDeleteAlias() {
  return useSearchMutation(({ entity, aliasId }: { entity: AliasEntity; aliasId: number }) =>
    authedClient.request<void>(`/v1/admin/search/aliases/${entity}/${aliasId}`, {
      method: 'DELETE',
    }),
  );
}

export function useZeroResults(days: number, page = 1) {
  return useQuery({
    queryKey: keys.search.zeroResults({ days, page }),
    queryFn: () =>
      authedClient.request<ZeroResultList>(
        `/v1/admin/search/zero-results?days=${days}&page=${page}&page_size=50`,
      ),
  });
}

/** The whole report as CSV for a spreadsheet; read as text, never cached (02-architecture/13 §9). */
export function useZeroResultsCsv() {
  return useMutation({
    mutationFn: (days: number) =>
      authedClient.request<string>(`/v1/admin/search/zero-results.csv?days=${days}`, {
        parse: 'text',
      }),
  });
}

export function usePromoteZeroResult() {
  return useSearchMutation(
    (body: { query_norm: string; entity: AliasEntity; entity_id: number }) =>
      authedClient.request<AliasRow>('/v1/admin/search/zero-results/promote', {
        method: 'POST',
        body,
      }),
  );
}
